// tour-check.component.ts
import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { UserService } from '../../../users/services/user.service'
import { PaginatedResponseModel } from '../../../../core/models/paginated-response.model'
import { UserModel } from '../../../../core/models/user.model'
import { DatePipe, NgClass, NgFor, NgForOf, NgIf } from '@angular/common'
import { Select } from 'primeng/select'
import { DropdownModule } from 'primeng/dropdown'
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import { CalendarOptions } from '@fullcalendar/core/index.js'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'
import { concat, finalize, tap, zip } from 'rxjs'
import { map } from 'rxjs/operators'
import { EventClickArg } from '@fullcalendar/core'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { EventsService } from '../../../events/services/events.service'
import { FormsModule } from '@angular/forms'
import { DateTools } from '../../../../core/tools/date-tools'
import { RouteTools } from '../../../../core/tools/route-tools'
import { LeafletModule } from '@bluehalo/ngx-leaflet'
import { GeoJSON, latLng, tileLayer } from 'leaflet'
import * as L from 'leaflet'
import { Tag } from 'primeng/tag'
import { Button, ButtonDirective } from 'primeng/button'
import { RouteMapComponent } from '../route-map/route-map.component'
import { Dialog } from 'primeng/dialog'
import { Tooltip } from 'primeng/tooltip'
import { Calendar } from 'primeng/calendar'
import { ToastService } from '../../../../shared/services/toast.service'
import { ActivatedRoute } from '@angular/router'
import { OptimizationService } from '../../services/optimization.service'
import { ConfirmationService } from 'primeng/api'
import { ConfirmDialog } from 'primeng/confirmdialog'

@Component({
  selector: 'app-tour-check',
  templateUrl: './tour-check.component.html',
  standalone: true,
  styleUrls: ['./tour-check.component.scss'],
  imports: [
    DropdownModule,
    FullCalendarModule,
    FormsModule,
    NgIf,
    NgFor,
    NgClass,
    LeafletModule,
    Tag,
    Button,
    DatePipe,
    RouteMapComponent,
    Dialog,
    Tooltip,
    ButtonDirective,
    Calendar,
    ConfirmDialog,
  ],
  providers: [ConfirmationService],
})
export class TourCheckComponent implements OnInit {
  protected users: (UserModel & { label: string; value: number })[] = []
  protected routeInfo: any = null
  protected optimizedRouteInfo: any = null
  protected interventionInfo: any = null
  protected allInterventions: any[] = [] // Stocker toutes les interventions
  protected distanceMatrix: any = null // Stocker la matrice de distances
  protected selectedDate: Date = new Date() // Date sélectionnée pour le calendrier

  @ViewChild('leafletMap', { static: false }) leafletMapEl!: any
  private mapInstance: L.Map | null = null

  @ViewChild('calendar') calendar!: FullCalendarComponent
  @ViewChild('routeMap') routeMapComponent!: RouteMapComponent
  private loadingEvents: boolean
  protected loadingOptimizedRoute: boolean = false
  protected swappingInterventionIndex: number | null = null
  private pendingOptimalCheck: boolean = false
  protected applyingAllModifications: boolean = false

  onMapReady(map: L.Map): void {
    this.mapInstance = map
  }

  public calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'timeGridDay',
    locale: frLocale,
    nowIndicator: true,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    slotDuration: '00:30:00',
    height: 'auto',
    selectable: false,
    events: [],
    editable: true, // permet drag & drop
    eventDurationEditable: false, // (optionnel) interdit resize
    headerToolbar: {
      left: '',
      center: '',
      right: '',
    },
    eventDrop: (info) => this.onEventDropped(info),
    datesSet: () => {
      // Ne charger les événements que si un utilisateur est sélectionné
      if (this.selectedUser) {
        this.loadEventsForToday()
        // Mettre à jour la date sélectionnée
        this.selectedDate = new Date(this.calendar.getApi().getDate())
      }
    },
    eventClick: (info) => this.handleEventClick(info),
  }
  selectedUser: any

  // Getter pour récupérer l'objet utilisateur complet
  get selectedUserObject() {
    return this.users.find((user) => user.value === this.selectedUser) || null
  }

  layers: (GeoJSON | L.Marker)[] = []
  options = {
    layers: [
      tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution: '',
        }
      ),
    ],
    zoom: 6,
    center: latLng(46.5, 2.5),
  }

  ngOnInit(): void {
    this.userService.getUsersWithFilters({}).subscribe((users) => {
      // Transformer les utilisateurs pour le format attendu par le dropdown
      this.users = users.map((user) => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
        ...user, // Conserver toutes les propriétés de l'utilisateur
      }))

      // Gérer les paramètres de redirection depuis optimized-list
      this.route.queryParams.subscribe((params) => {
        console.log('Query params reçus:', params)

        if (params['userId'] && params['date']) {
          // Utiliser l'ID du technicien (plus fiable)
          const technicianId = +params['userId']
          const targetDate = params['date']

          console.log('Recherche technicien avec ID:', technicianId)
          console.log('Liste des utilisateurs:', this.users)

          const foundUser = this.users.find(
            (user) => user.value === technicianId
          )

          console.log('Utilisateur trouvé:', foundUser)
          if (foundUser) {
            this.selectedUser = foundUser.value
            console.log('selectedUser défini à:', this.selectedUser)
            this.updateUserSelect()

            // Configurer le calendrier pour la date spécifiée
            this.setCalendarDate(targetDate)
            this.loadEventsForToday()
          } else {
            console.warn(
              `Technicien avec l'ID "${technicianId}" non trouvé dans la liste`
            )
          }
        } else if (params['user'] && params['date']) {
          // Fallback: utiliser le nom du technicien
          const technicianName = params['user']
          const targetDate = params['date']

          const foundUser = this.users.find(
            (user) =>
              user.label &&
              user.label.toLowerCase() === technicianName.toLowerCase()
          )

          if (foundUser) {
            this.selectedUser = foundUser.value
            this.updateUserSelect()

            // Configurer le calendrier pour la date spécifiée
            this.setCalendarDate(targetDate)
            this.loadEventsForToday()
          } else {
            console.warn(
              `Technicien "${technicianName}" non trouvé dans la liste`
            )
          }
        } else {
          // Comportement par défaut - utiliser l'utilisateur stocké
          const storedUser = localStorage.getItem('tourUserSelected')
          if (storedUser) {
            this.selectedUser = +storedUser
            this.loadEventsForToday()
          }
        }
      })
    })
  }

  constructor(
    private userService: UserService,
    private interventionsService: InterventionsService,
    private eventsService: EventsService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private optimizationService: OptimizationService,
    private confirmationService: ConfirmationService
  ) {}

  protected loadEventsForToday(): void {
    // Vérifier que selectedUser est défini avant de continuer
    if (!this.selectedUser) {
      console.warn(
        'selectedUser non défini, abandon du chargement des événements'
      )
      return
    }

    const calendarApi = this.calendar.getApi()
    const currentDate = calendarApi.getDate()

    this.interventionInfo = null
    this.routeInfo = null
    this.optimizedRouteInfo = null
    this.distanceMatrix = null

    const start = new Date(currentDate)
    const end = new Date(currentDate)

    const params = {
      users: [this.selectedUser],
      dateFrom: DateTools.formatDateForAPI(start),
      dateTo: DateTools.formatDateForAPI(end),
    }

    console.log('Intervention params:', params)

    // Afficher le loading sur la carte
    if (this.routeMapComponent) {
      this.routeMapComponent.showLoading()
    }

    zip([this.interventionsService.getInterventions(params)])
      .pipe(
        map(([interventions]) => [...interventions]),
        tap((allEvents) => {
          this.calendarOptions.events = allEvents
          this.allInterventions = allEvents // Stocker les interventions

          // Compteur pour savoir quand les trois appels API sont chargés
          let routesLoaded = 0
          const totalRoutes = 3

          const checkAllRoutesLoaded = () => {
            routesLoaded++
            if (routesLoaded >= totalRoutes) {
              if (this.routeMapComponent) {
                this.routeMapComponent.hideLoading()
              }
              // Vérifier si l'itinéraire est devenu optimal après une modification
              if (this.pendingOptimalCheck) {
                this.pendingOptimalCheck = false
                if (this.isRouteOptimal()) {
                  this.removeOptimizationResult()
                }
              }
            }
          }

          // Appel pour calculer les distances
          this.interventionsService
            .calculateDistances(this.selectedUser, allEvents)
            .subscribe({
              next: (distances: any) => {
                this.distanceMatrix = distances
                console.log('Distance matrix received:', this.distanceMatrix)
                checkAllRoutesLoaded()
              },
              error: (error) => {
                console.error('Error fetching distances:', error)
                this.distanceMatrix = null
                checkAllRoutesLoaded()
              },
            })

          this.interventionsService
            .getRoute(this.selectedUser, allEvents)
            .subscribe({
              next: (route: any) => {
                this.layers = []
                this.routeInfo = route
                console.log('Route data received:', this.routeInfo)
                checkAllRoutesLoaded()
              },
              error: (error) => {
                console.error('Error fetching route:', error)
                this.routeInfo = null
                checkAllRoutesLoaded()
              },
            })
          this.loadingOptimizedRoute = true
          this.interventionsService
            .getOptimizedRoute(this.selectedUser, allEvents)
            .subscribe({
              next: (route: any) => {
                this.layers = []
                this.optimizedRouteInfo = route
                console.log(
                  'Optimized route data received:',
                  this.optimizedRouteInfo
                )
                this.loadingOptimizedRoute = false
                checkAllRoutesLoaded()
              },
              error: (error) => {
                console.error('Error fetching optimized route:', error)
                this.optimizedRouteInfo = null
                this.loadingOptimizedRoute = false
                checkAllRoutesLoaded()
              },
            })
        })
      )
      .subscribe()
  }

  protected selectedIntervention: any = null

  /**
   * Gère le clic sur une intervention dans la timeline récap
   */
  protected handleTimelineClick(intervention: any): void {
    this.selectedIntervention = intervention.extendedProps || intervention
    const data = this.selectedIntervention?.data || this.selectedIntervention

    if (data?.id && data?.erpSource) {
      this.interventionsService.show(data.id, data.erpSource).subscribe({
        next: (value) => {
          this.interventionInfo = value
        },
        error: (err) => {
          console.error('Error fetching intervention details:', err)
        },
      })
    }
  }

  protected updateUserSelect() {
    localStorage.setItem('tourUserSelected', this.selectedUser)
  }

  // Méthodes pour la navigation de date
  protected goToPreviousDay(): void {
    const calendarApi = this.calendar.getApi()
    calendarApi.prev()
    this.selectedDate = new Date(calendarApi.getDate())
  }

  protected goToNextDay(): void {
    const calendarApi = this.calendar.getApi()
    calendarApi.next()
    this.selectedDate = new Date(calendarApi.getDate())
  }

  protected goToToday(): void {
    const calendarApi = this.calendar.getApi()
    calendarApi.today()
    this.selectedDate = new Date()
  }

  protected onDateChange(): void {
    if (this.selectedDate) {
      const calendarApi = this.calendar.getApi()
      calendarApi.gotoDate(this.selectedDate)
      // Les événements seront rechargés automatiquement via datesSet
    }
  }

  protected getFormattedDate(): string {
    const calendarApi = this.calendar?.getApi()
    if (calendarApi) {
      const currentDate = calendarApi.getDate()
      return currentDate.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
    return this.selectedDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  private setCalendarDate(dateString: string): void {
    try {
      const targetDate = new Date(dateString)
      if (!isNaN(targetDate.getTime())) {
        this.selectedDate = targetDate
        const calendarApi = this.calendar?.getApi()
        if (calendarApi) {
          calendarApi.gotoDate(targetDate)
        }
      } else {
        console.warn(`Date invalide: ${dateString}`)
      }
    } catch (error) {
      console.warn(
        `Erreur lors de la configuration de la date: ${dateString}`,
        error
      )
    }
  }

  handleEventClick(info: EventClickArg): void {
    this.selectedIntervention = info.event.extendedProps
    console.log(this.selectedIntervention)

    this.interventionsService
      .show(
        this.selectedIntervention['data'].id,
        this.selectedIntervention['data'].erpSource
      )
      .subscribe({
        next: (value) => {
          this.interventionInfo = value
          console.log('Intervention details:', this.interventionInfo)
        },
        error: (err) => {
          console.error('Error fetching intervention details:', err)
        },
      })
  }

  protected showRouteDetailsModal: boolean = false

  protected openRouteDetailsModal(): void {
    this.showRouteDetailsModal = true
  }

  protected closeRouteDetailsModal(): void {
    this.showRouteDetailsModal = false
  }

  formatDuration(durationMin: number): string {
    const hours = Math.floor(durationMin / 60)
    const minutes = Math.round(durationMin % 60)

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h`
    } else {
      return `${minutes}m`
    }
  }

  protected openGoogleMapsRoute(): void {
    if (
      !this.selectedUserObject ||
      !this.allInterventions ||
      this.allInterventions.length === 0
    ) {
      this.toastService.showError(
        "Impossible d'ouvrir l'itinéraire : données manquantes"
      )
      return
    }

    // Obtenir la date actuelle du calendrier
    const calendarApi = this.calendar.getApi()
    const currentDate = calendarApi.getDate()

    try {
      // Générer l'URL Google Maps avec RouteTools
      const googleMapsUrl = RouteTools.generateDailyRouteUrl(
        this.selectedUserObject,
        currentDate,
        this.allInterventions
      )

      if (googleMapsUrl) {
        // Ouvrir dans un nouvel onglet
        window.open(googleMapsUrl, '_blank')
        this.toastService.showSuccess(
          "Ouverture de l'itinéraire dans Google Maps"
        )
      } else {
        this.toastService.showError("Impossible de générer l'itinéraire")
      }
    } catch (error) {
      console.error("Erreur lors de la génération de l'URL Google Maps:", error)
      this.toastService.showError("Erreur lors de l'ouverture de l'itinéraire")
    }
  }

  protected openOptimizedGoogleMapsRoute(): void {
    if (
      !this.selectedUserObject ||
      !this.optimizedRouteInfo?.optimizedOrder ||
      this.optimizedRouteInfo.optimizedOrder.length === 0
    ) {
      this.toastService.showError(
        "Impossible d'ouvrir l'itinéraire optimisé : données manquantes"
      )
      return
    }

    try {
      // Construire l'URL Google Maps avec l'ordre optimisé
      let mapsUrl = 'https://www.google.com/maps/dir/?api=1'

      // Formater l'adresse du domicile du technicien
      const technicianAddress = this.formatTechnicianAddress(
        this.selectedUserObject
      )

      // Utiliser le domicile du technicien comme origine et destination
      mapsUrl += `&origin=${technicianAddress}`
      mapsUrl += `&destination=${technicianAddress}`

      // Ajouter les interventions dans l'ordre optimisé comme waypoints
      if (this.optimizedRouteInfo.optimizedOrder.length > 0) {
        mapsUrl += '&waypoints='
        const waypoints = this.optimizedRouteInfo.optimizedOrder
          .map((order: any) => this.formatOptimizedInterventionAddress(order))
          .join('|')
        mapsUrl += waypoints
      }

      // Ajouter le mode de transport
      mapsUrl += '&travelmode=driving'

      if (mapsUrl) {
        // Ouvrir dans un nouvel onglet
        window.open(mapsUrl, '_blank')
        this.toastService.showSuccess(
          "Ouverture de l'itinéraire optimisé dans Google Maps"
        )
      } else {
        this.toastService.showError(
          "Impossible de générer l'itinéraire optimisé"
        )
      }
    } catch (error) {
      console.error(
        "Erreur lors de la génération de l'URL Google Maps optimisée:",
        error
      )
      this.toastService.showError(
        "Erreur lors de l'ouverture de l'itinéraire optimisé"
      )
    }
  }

  private formatTechnicianAddress(technician: any): string {
    const address = [technician.postalCode || '', technician.locality || '']
      .filter(Boolean)
      .join(', ')
    return encodeURIComponent(address)
  }

  private formatOptimizedInterventionAddress(order: any): string {
    const address = [order.postalCode || '', order.locality || '']
      .filter(Boolean)
      .join(', ')
    return encodeURIComponent(address)
  }

  /**
   * Vérifie si l'intervention à l'index donné a le flag doNotShift actif
   */
  protected isDoNotShift(originalIndex: number): boolean {
    const intervention = this.allInterventions[originalIndex]
    return (
      intervention?.data?.doNotShift === 1 ||
      intervention?.data?.doNotShift === true
    )
  }

  /**
   * Retourne le nombre de modifications nécessaires
   */
  protected getModificationsCount(): number {
    if (!this.optimizedRouteInfo?.optimizedOrder) return 0
    return this.optimizedRouteInfo.optimizedOrder.filter(
      (order: any) => order.position !== order.originalIndex
    ).length
  }

  /**
   * Applique toutes les modifications suggérées après confirmation
   */
  protected confirmApplyAllModifications(): void {
    const count = this.getModificationsCount()
    if (count === 0) return

    this.confirmationService.confirm({
      message: `Voulez-vous appliquer les ${count} modification${count > 1 ? 's' : ''} suggérée${count > 1 ? 's' : ''} ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Appliquer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.applyAllModifications()
      },
    })
  }

  /**
   * Applique toutes les modifications en séquence
   * Chaque intervention est déplacée au créneau de sa position optimisée
   */
  private applyAllModifications(): void {
    if (!this.optimizedRouteInfo?.optimizedOrder) return

    const modifications = this.optimizedRouteInfo.optimizedOrder.filter(
      (order: any) => order.position !== order.originalIndex
    )

    if (modifications.length === 0) return

    this.applyingAllModifications = true

    // Construire la liste des interventions dans l'ordre optimisé
    // On assigne à chaque intervention le créneau horaire correspondant à sa nouvelle position
    const sortedByPosition = [...this.optimizedRouteInfo.optimizedOrder].sort(
      (a: any, b: any) => a.position - b.position
    )

    // Créer les paires : intervention -> créneau cible
    const updates: { intervention: any; targetStart: Date; targetEnd: Date }[] =
      []

    for (const order of sortedByPosition) {
      const intervention = this.allInterventions[order.originalIndex]
      // Le créneau cible est celui de l'intervention qui est actuellement à la position visée
      const targetSlotIntervention = this.allInterventions[order.position]

      if (
        intervention &&
        targetSlotIntervention &&
        order.position !== order.originalIndex
      ) {
        const duration =
          new Date(intervention.end).getTime() -
          new Date(intervention.start).getTime()
        const targetStart = new Date(targetSlotIntervention.start)
        const targetEnd = new Date(targetStart.getTime() + duration)

        updates.push({ intervention, targetStart, targetEnd })
      }
    }

    if (updates.length === 0) {
      this.applyingAllModifications = false
      return
    }

    // Créer les observables séquentiels
    const updateObservables = updates.map((update) => {
      const params = {
        users: [this.selectedUser],
        startDate: DateTools.toLocalISOString(update.targetStart),
        erpSource: update.intervention.data.erpSource,
        duration: DateTools.calcTimeDifference(
          update.targetStart,
          update.targetEnd
        ).toTimeString(),
      }
      return this.interventionsService.updateDate(
        update.intervention.data.id,
        params
      )
    })

    // Exécuter séquentiellement
    concat(...updateObservables)
      .pipe(
        finalize(() => {
          this.applyingAllModifications = false
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          this.toastService.showError(
            "Erreur lors de l'application des modifications"
          )
          console.error(err)
        },
        complete: () => {
          this.toastService.showSuccess(
            'Toutes les modifications ont été appliquées'
          )
          this.pendingOptimalCheck = true
          this.loadEventsForToday()
        },
      })
  }

  protected moveIntervention(oldIndex: number, newIndex: number): void {
    const interventions = [...this.allInterventions]

    const movedItem = interventions[oldIndex]
    const displacedItem = interventions[newIndex]

    if (
      !movedItem ||
      !movedItem.start ||
      !movedItem.end ||
      !displacedItem ||
      !displacedItem.start ||
      !displacedItem.end
    ) {
      console.error('Intervention(s) invalide(s)', { movedItem, displacedItem })
      return
    }

    const movedDurationMs =
      new Date(movedItem.end).getTime() - new Date(movedItem.start).getTime()
    const displacedDurationMs =
      new Date(displacedItem.end).getTime() -
      new Date(displacedItem.start).getTime()

    const newStartForMoved = new Date(displacedItem.start)
    const newEndForMoved = new Date(
      newStartForMoved.getTime() + movedDurationMs
    )

    const newStartForDisplaced = new Date(movedItem.start)
    const newEndForDisplaced = new Date(
      newStartForDisplaced.getTime() + displacedDurationMs
    )

    const paramsMoved = {
      users: [this.selectedUser],
      startDate: DateTools.toLocalISOString(newStartForMoved),
      erpSource: movedItem.data.erpSource,
      duration: DateTools.calcTimeDifference(
        newStartForMoved,
        newEndForMoved
      ).toTimeString(),
    }

    const paramsDisplaced = {
      users: [this.selectedUser],
      startDate: DateTools.toLocalISOString(newStartForDisplaced),
      erpSource: displacedItem.data.erpSource,
      duration: DateTools.calcTimeDifference(
        newStartForDisplaced,
        newEndForDisplaced
      ).toTimeString(),
    }

    this.loadingEvents = true
    this.swappingInterventionIndex = oldIndex

    this.interventionsService
      .updateDate(movedItem.data.id, paramsMoved)
      .pipe(
        tap(() => {
          // 2e update, enchaînée après le succès de la première
          this.interventionsService
            .updateDate(displacedItem.data.id, paramsDisplaced)
            .pipe(
              finalize(() => {
                this.loadingEvents = false
                this.swappingInterventionIndex = null
              })
            )
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  'Interventions inversées avec succès'
                )
                this.pendingOptimalCheck = true
                this.loadEventsForToday()
              },
              error: (err) => {
                this.toastService.showError(
                  'Erreur en inversant la 2e intervention'
                )
                console.error(err)
              },
            })
        }),
        finalize(() => {
          if (!this.loadingEvents) {
            this.loadingEvents = false
            this.swappingInterventionIndex = null
          }
        })
      )
      .subscribe({
        error: (err) => {
          this.toastService.showError(
            'Erreur en mettant à jour la 1ère intervention'
          )
          this.swappingInterventionIndex = null
          console.error(err)
        },
      })
  }

  protected onEventDropped(info: any): void {
    const event = info.event

    const interventionId = event.extendedProps.data.id
    const erpSource = event.extendedProps.data.erpSource

    const startDate = DateTools.toLocalISOString(event.start)
    const endDate = DateTools.toLocalISOString(event.end)
    const duration = DateTools.calcTimeDifference(
      new Date(startDate),
      new Date(endDate)
    )

    const params = {
      users: [this.selectedUser],
      startDate: startDate,
      erpSource: erpSource,
      duration: duration.toTimeString(),
    }

    this.loadingEvents = true

    this.interventionsService
      .updateDate(interventionId, params)
      .pipe(finalize(() => (this.loadingEvents = false)))
      .subscribe({
        next: () => {
          this.toastService.showSuccess('Intervention déplacée avec succès')
          this.pendingOptimalCheck = true
          this.loadEventsForToday()
        },
        error: (err) => {
          this.toastService.showError('Erreur lors du déplacement')
          info.revert() // Remet l'event à sa position d'origine si erreur
          console.error(err)
        },
      })
  }

  /**
   * Supprime l'entrée d'optimisation correspondante à la journée et au technicien actuel
   */
  private removeOptimizationResult(): void {
    if (!this.selectedUser || !this.calendar) return

    const calendarApi = this.calendar.getApi()
    const currentDate = calendarApi.getDate()
    const dateStr = DateTools.formatDateForAPI(new Date(currentDate))

    this.optimizationService
      .deleteOptimizationResult(this.selectedUser, dateStr)
      .subscribe({
        next: () => {
          console.log("Résultat d'optimisation supprimé")
        },
        error: (err) => {
          console.error(
            "Erreur lors de la suppression du résultat d'optimisation:",
            err
          )
        },
      })
  }

  /**
   * Obtient les informations de distance entre deux points spécifiques
   */
  protected getDistanceBetweenPoints(fromIndex: number, toIndex: number): any {
    if (
      !this.distanceMatrix?.matrix ||
      !this.distanceMatrix.matrix[fromIndex]
    ) {
      return null
    }
    return this.distanceMatrix.matrix[fromIndex][toIndex] || null
  }

  /**
   * Obtient le nom d'un point à partir de son index
   */
  protected getPointName(index: number): string {
    if (!this.distanceMatrix?.points || !this.distanceMatrix.points[index]) {
      return `Point ${index + 1}`
    }
    return this.distanceMatrix.points[index].name || `Point ${index + 1}`
  }

  /**
   * Calcule la distance totale et le temps total pour l'itinéraire actuel
   */
  protected getCurrentRouteDistance(): { distance: number; duration: number } {
    if (!this.distanceMatrix?.matrix || !this.allInterventions.length) {
      return { distance: 0, duration: 0 }
    }

    let totalDistance = 0
    let totalDuration = 0

    // Calculer la séquence actuelle basée sur l'ordre des interventions
    const points = this.distanceMatrix.points
    if (!points) return { distance: 0, duration: 0 }

    // Trouver l'index du point de départ
    const startIndex = points.findIndex((p: any) => p.type === 'start')
    if (startIndex === -1) return { distance: 0, duration: 0 }

    let currentIndex = startIndex

    // Parcourir les interventions dans l'ordre chronologique
    const sortedInterventions = [...this.allInterventions].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    )

    for (const intervention of sortedInterventions) {
      const interventionIndex = points.findIndex(
        (p: any) => p.name === intervention.title && p.type === 'intervention'
      )

      if (interventionIndex !== -1) {
        const distance = this.getDistanceBetweenPoints(
          currentIndex,
          interventionIndex
        )
        if (distance) {
          totalDistance += distance.distance_km
          totalDuration += distance.duration_min
        }
        currentIndex = interventionIndex
      }
    }

    // Retour au point d'arrivée
    const endIndex = points.findIndex((p: any) => p.type === 'end')
    if (endIndex !== -1 && currentIndex !== endIndex) {
      const distance = this.getDistanceBetweenPoints(currentIndex, endIndex)
      if (distance) {
        totalDistance += distance.distance_km
        totalDuration += distance.duration_min
      }
    }

    return { distance: totalDistance, duration: totalDuration }
  }

  /**
   * Obtient les détails de chaque étape de l'itinéraire avec distances
   */
  protected getRouteStepsWithDistances(): any[] {
    if (!this.distanceMatrix?.matrix || !this.allInterventions.length) {
      return []
    }

    const steps: any[] = []
    const points = this.distanceMatrix.points
    if (!points) return []

    const startIndex = points.findIndex((p: any) => p.type === 'start')
    if (startIndex === -1) return []

    // Ajouter le point de départ comme première étape
    steps.push({
      from: null,
      to: this.getPointName(startIndex),
      intervention: null,
      distance: 0,
      duration: 0,
      stepNumber: 1,
      interventionNumber: null,
      isStart: true,
    })

    let currentIndex = startIndex

    // Trier les interventions par heure de début
    const sortedInterventions = [...this.allInterventions].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    )

    for (let i = 0; i < sortedInterventions.length; i++) {
      const intervention = sortedInterventions[i]
      const interventionIndex = points.findIndex(
        (p: any) => p.name === intervention.title && p.type === 'intervention'
      )

      if (interventionIndex !== -1) {
        const distance = this.getDistanceBetweenPoints(
          currentIndex,
          interventionIndex
        )

        steps.push({
          from: this.getPointName(currentIndex),
          to: this.getPointName(interventionIndex),
          intervention: intervention,
          distance: distance?.distance_km || 0,
          duration: distance?.duration_min || 0,
          stepNumber: steps.length + 1,
          interventionNumber: i + 1,
        })

        currentIndex = interventionIndex
      }
    }

    // Étape de retour
    const endIndex = points.findIndex((p: any) => p.type === 'end')
    if (endIndex !== -1) {
      const distance = this.getDistanceBetweenPoints(currentIndex, endIndex)
      steps.push({
        from: this.getPointName(currentIndex),
        to: this.getPointName(endIndex),
        intervention: null,
        distance: distance?.distance_km || 0,
        duration: distance?.duration_min || 0,
        stepNumber: steps.length + 1,
        interventionNumber: null,
        isReturn: true,
      })
    }

    return steps
  }

  /**
   * Obtient le texte du tooltip pour une étape
   */
  protected getStepTooltip(step: any): string | undefined {
    if (step.isStart) {
      return 'Point de départ'
    }
    if (step.isReturn) {
      return 'Point de retour'
    }
    if (step.intervention?.data) {
      const inter = step.intervention.data
      const locality = inter.workSiteLocality || ''
      const postalCode = inter.workSitePostalCode || ''
      const title = step.intervention.title || ''

      if (locality && postalCode) {
        return `${title}\n${locality} (${postalCode})`
      } else if (locality) {
        return `${title}\n${locality}`
      } else {
        return title
      }
    }
    return undefined
  }

  /**
   * Vérifie si l'itinéraire est déjà optimal (aucune modification suggérée)
   */
  protected isRouteOptimal(): boolean {
    if (!this.optimizedRouteInfo || !this.routeInfo) {
      return false
    }

    return (
      this.routeInfo.summary.distance_km <=
        this.optimizedRouteInfo.summary.distance_km &&
      this.routeInfo.summary.duration_min <=
        this.optimizedRouteInfo.summary.duration_min
    )
  }

  /**
   * Génère un récapitulatif de la journée avec heures de départ/arrivée estimées
   */
  protected getDailySummary(): {
    departureTime: Date | null
    arrivalTime: Date | null
    steps: {
      type: 'home-departure' | 'intervention' | 'home-arrival'
      time: Date | null
      endTime: Date | null
      title: string
      locality: string
      travelDuration: number
      interventionDuration: number
      distance: number
      interventionNumber: number | null
      intervention: any
    }[]
    totalDistance: number
    totalDuration: number
    totalInterventionTime: number
  } {
    const result = {
      departureTime: null as Date | null,
      arrivalTime: null as Date | null,
      steps: [] as any[],
      totalDistance: 0,
      totalDuration: 0,
      totalInterventionTime: 0,
    }

    if (!this.allInterventions || this.allInterventions.length === 0) {
      return result
    }

    const sortedInterventions = [...this.allInterventions].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    )

    const routeSteps = this.getRouteStepsWithDistances()
    const routeDistance = this.getCurrentRouteDistance()
    result.totalDistance = routeDistance.distance
    result.totalDuration = routeDistance.duration

    // Calculer le temps total d'intervention
    for (const intervention of sortedInterventions) {
      const start = new Date(intervention.start)
      const end = new Date(intervention.end)
      result.totalInterventionTime += (end.getTime() - start.getTime()) / 60000
    }

    // Trouver le trajet domicile -> première intervention
    const firstTravelStep = routeSteps.find(
      (s) => !s.isStart && !s.isReturn && s.interventionNumber === 1
    )
    const firstTravelDuration = firstTravelStep ? firstTravelStep.duration : 0
    const firstTravelDistance = firstTravelStep ? firstTravelStep.distance : 0

    // Heure de départ du domicile
    const firstIntervention = sortedInterventions[0]
    const firstStart = new Date(firstIntervention.start)
    const departureTime = new Date(
      firstStart.getTime() - firstTravelDuration * 60000
    )
    result.departureTime = departureTime

    // Étape départ domicile
    result.steps.push({
      type: 'home-departure',
      time: departureTime,
      endTime: null,
      title: 'Départ domicile',
      locality: this.selectedUserObject
        ? `${this.selectedUserObject.locality || ''}`
        : '',
      travelDuration: 0,
      interventionDuration: 0,
      distance: 0,
      interventionNumber: null,
      intervention: null,
    })

    // Étapes interventions
    for (let i = 0; i < sortedInterventions.length; i++) {
      const intervention = sortedInterventions[i]
      const start = new Date(intervention.start)
      const end = new Date(intervention.end)
      const durationMin = (end.getTime() - start.getTime()) / 60000

      // Trouver le trajet correspondant
      let travelDuration = 0
      let travelDistance = 0
      if (i === 0) {
        travelDuration = firstTravelDuration
        travelDistance = firstTravelDistance
      } else {
        const step = routeSteps.find(
          (s) => !s.isStart && !s.isReturn && s.interventionNumber === i + 1
        )
        if (step) {
          travelDuration = step.duration
          travelDistance = step.distance
        }
      }

      result.steps.push({
        type: 'intervention',
        time: start,
        endTime: end,
        title:
          intervention.title ||
          intervention.data?.name ||
          `Intervention ${i + 1}`,
        locality:
          intervention.data?.workSiteLocality ||
          intervention.data?.workSitePostalCode ||
          '',
        travelDuration: travelDuration,
        interventionDuration: durationMin,
        distance: travelDistance,
        interventionNumber: i + 1,
        intervention: intervention,
      })
    }

    // Trouver le trajet dernière intervention -> domicile
    const returnStep = routeSteps.find((s) => s.isReturn)
    const returnTravelDuration = returnStep ? returnStep.duration : 0
    const returnTravelDistance = returnStep ? returnStep.distance : 0

    // Heure d'arrivée au domicile
    const lastIntervention = sortedInterventions[sortedInterventions.length - 1]
    const lastEnd = new Date(lastIntervention.end)
    const arrivalTime = new Date(
      lastEnd.getTime() + returnTravelDuration * 60000
    )
    result.arrivalTime = arrivalTime

    // Étape retour domicile
    result.steps.push({
      type: 'home-arrival',
      time: arrivalTime,
      endTime: null,
      title: 'Retour domicile',
      locality: this.selectedUserObject
        ? `${this.selectedUserObject.locality || ''}`
        : '',
      travelDuration: returnTravelDuration,
      interventionDuration: 0,
      distance: returnTravelDistance,
      interventionNumber: null,
      intervention: null,
    })

    return result
  }

  /**
   * Formate une date en heures:minutes
   */
  protected formatTime(date: Date | null): string {
    if (!date) return '--:--'
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /** Toggle pour afficher/masquer le calendrier */
  protected showCalendar: boolean = true

  protected toggleCalendar(): void {
    this.showCalendar = !this.showCalendar
  }
}
